/**
 * HIPAA dashboard — RLS audit API client.
 *
 * All endpoints are POST + bearer token. The backend generates every SQL
 * statement itself, so nothing here ever sends SQL.
 */
import { getBackendURL } from "../components/env";
import { supabasePublic, supabaseSchema } from "../components/supabaseClient";

export const GRADE_COLORS = {
  green: "green",
  yellow: "yellow",
  red: "red",
  grey: "gray",
  absent: "gray",
};

async function post(path, body) {
  const { data: { session } = {} } = await supabasePublic().auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("No active session — sign in again");

  const res = await fetch(`${getBackendURL()}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body || {}),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.error) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json;
}

export function runRlsScan() {
  return post("/api/admin/rls/scan/", {});
}

export function previewRlsFix(schema, table) {
  return post("/api/admin/rls/fix-preview/", { schema, table });
}

export function applyRlsFix(schema, table) {
  return post("/api/admin/rls/fix-apply/", { schema, table });
}

export function previewRlsFixBatch(schema) {
  return post("/api/admin/rls/fix-batch-preview/", schema ? { schema } : {});
}

export function applyRlsFixBatch(schema) {
  return post("/api/admin/rls/fix-batch-apply/", schema ? { schema } : {});
}

export function fetchPhiDeidStatus() {
  // The Master Note row is graded for the schema you are signed into, whose
  // own DEID_MASTER_NOTE setting decides whether it scrubs.
  return post("/api/admin/phi-deid/status/", { schema: supabaseSchema });
}

export function testPhiDeidPath(pathId) {
  // The Master Note row runs the real engine for the schema you are signed into.
  return post("/api/admin/phi-deid/test/", { path_id: pathId, schema: supabaseSchema });
}

export function setMasterNoteDeid(schema, enabled) {
  // Writes schema_settings.settings.DEID_MASTER_NOTE for that schema and
  // returns the redrawn path list.
  return post("/api/admin/phi-deid/master-note-switch/", { schema, enabled });
}

export function fetchAuditLogs(filters = {}) {
  return post("/api/admin/audit-logs/list/", filters);
}

export function fetchAuditLogsRetention() {
  return post("/api/admin/audit-logs/retention/", {});
}

export function fetchAuditLogsStatus() {
  return post("/api/admin/audit-logs/status/", {});
}

export function fetchAuditLogsSummary(filters = {}) {
  return post("/api/admin/audit-logs/summary/", filters);
}

/** Auth failures happen before Supabase has a session, so this endpoint must
 *  allow an unauthenticated login-failure event. Identity is token-derived
 *  whenever a session exists; only a failed login can use the entered email. */
export async function recordAuthEvent(event, detail = {}) {
  try {
    const { data: { session } = {} } = await supabasePublic().auth.getSession();
    const failedLogin = event === "login" && detail.success === false;
    if (!session?.access_token && !failedLogin) return { recorded: false };
    const token = failedLogin ? null : session?.access_token;
    const res = await fetch(`${getBackendURL()}/api/admin/audit-logs/auth-event/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ event, ...detail }),
    });
    return await res.json().catch(() => ({ recorded: false }));
  } catch {
    return { recorded: false };
  }
}

/** Record a browser-side print/export/download before the browser releases PHI. */
export async function recordDataOutEvent(action_type, detail = {}) {
  try {
    return await post("/api/admin/audit-logs/data-out/", { action_type, ...detail });
  } catch {
    return { recorded: false };
  }
}

export function fetchImpersonationAudit(filters = {}) {
  return post("/api/admin/audit-logs/impersonations/", filters);
}

export function fetchAuthHardeningStatus() {
  return post("/api/admin/audit-logs/auth-status/", {});
}

export function fetchAuthHardeningUserStatus() {
  return post("/api/admin/audit-logs/auth-users/", {});
}

export function submitAccessReview(body) {
  return post("/api/admin/audit-logs/access-review/submit/", body || {});
}

export function fetchAccessReviewHistory(limit = 10) {
  return post("/api/admin/audit-logs/access-review/history/", { limit });
}
